import { Observable, merge } from 'rxjs';
import { PromptElementImplInterface } from './PromptElementImplInterface';
import { getRequired, getFormattedData, getConstraint, getIsDateTime, getIsNumeric } from './utils';
import { PromptElementController, DataStateChangeEvent } from '@sas-dvr/ltjs-bird';
import { map, filter, tap } from 'rxjs/operators';
import { DataUsage, PortableEvent } from '@sas-dvr/ltjs-commons';
import {
  TextFieldState,
  TextFieldData,
} from '@sas-dvr/internal-va-react-core/store/birdElement/textFieldState';
import { ofActionCreatorType } from '../../util/rxUtils';
import { AnyAction } from 'redux';
import { getFormatInstance } from '@sas-dvr/ltjs-sasicu/util/SASFormatterUtil';

export class TextFieldImpl implements PromptElementImplInterface {
  private _controller: PromptElementController;
  private _id: string;
  private _isNumeric: boolean;
  private _isDateTime: boolean;
  private _isCategorical: boolean;
  private _minValue: number;
  private _maxValue: number;
  private _formatName?: string;
  private _required: boolean;
  private _ignoreValueChange: boolean = false;

  constructor(controller: PromptElementController, id: string) {
    this._controller = controller;
    this._id = id;
    this._isNumeric = getIsNumeric(controller);
    this._isDateTime = getIsDateTime(controller);
    this._isCategorical = false; // Not known until first data change
    this._formatName = controller.getFormatName() ?? undefined;
    this._required = getRequired(controller);

    const constraint = getConstraint(this._controller);
    this._minValue = constraint?.getMin() ? Number(constraint.getMin()) : NaN;
    this._maxValue = constraint?.getMax() ? Number(constraint.getMax()) : NaN;
  }

  createElement() {
    return TextFieldState.actions.add(this._id, {
      ...getFormattedData(this._controller),
      formattedValue: this.getFormattedValue(),
      committedValue: this.getRawValue(),
      error: null,
    });
  }

  deleteElement() {
    return TextFieldState.actions.delete(this._id);
  }

  handleDataChange(dataChange$: Observable<DataStateChangeEvent>) {
    return dataChange$.pipe(
      tap(() => {
        this._isCategorical =
          this._controller.getColumn()?.getDataUsage() === DataUsage.CATEGORICAL;
      }),
      map(() => {
        return TextFieldState.actions.update(this._id, {
          ...getFormattedData(this._controller),
          formattedValue: this.getFormattedValue(),
          committedValue: this.getRawValue(),
        });
      })
    );
  }

  handleValueChanged(valueChange$: Observable<PortableEvent>) {
    return valueChange$.pipe(
      filter(() => !this._ignoreValueChange),
      map(() => {
        return TextFieldState.actions.update(this._id, {
          formattedValue: this.getFormattedValue(),
          committedValue: this.getRawValue(),
        });
      })
    );
  }

  handleActions(myActions$: Observable<AnyAction>) {
    return merge(
      myActions$.pipe(
        ofActionCreatorType<typeof TextFieldState.actions.commitValue>(
          TextFieldState.actionTypes.commitValue
        ),
        map((action) => {
          const userInput = action.payload.data;
          const error = this.validateUserInput(userInput, { updateController: true });
          const committedValue =
            !error && (this._isDateTime || this._isCategorical) ? this.getRawValue() : userInput;
          return TextFieldState.actions.update(this._id, {
            committedValue,
            formattedValue: this.getFormattedValue(),
            error,
          });
        })
      ),
      myActions$.pipe(
        ofActionCreatorType<typeof TextFieldState.actions.updateError>(
          TextFieldState.actionTypes.updateError
        ),
        map((action) => {
          const error =
            this._isNumeric && !this._isCategorical
              ? // Character validation for numeric inputs
                this.validateUserInput(action.payload.data, { updateController: false })
              : // Value validation for all other types, so just clear the error
                null;

          return TextFieldState.actions.update(this._id, { error });
        })
      )
    );
  }

  getFormattedValue(): string {
    return this._controller.getCurrentValue()?.getDisplayString() ?? '';
  }

  getRawValue(): string {
    const currentValue = this._controller.getCurrentValue();
    if (!currentValue) {
      return '';
    }
    if (this._isDateTime || this._isCategorical) {
      // For DateTime and categorical data, display the formatted value
      return this.getFormattedValue();
    }
    if (this._isNumeric) {
      // S1542527: Format the numerical value for i18n
      // Example: 0.8 should be 0,8 in 'fr' locale
      return getFormatInstance('BEST32.')?.format(currentValue.getNumericValue()) ?? '';
    }
    return currentValue.getStringValue() ?? '';
  }

  validateUserInput(
    userInput: string,
    { updateController } = { updateController: false }
  ): TextFieldData['error'] {
    let valueToCommit = userInput;

    // For numeric inputs, parse to a number and give portable the corresponding formatted value.
    // This is needed because some formats change the value of the input, and some can't parse all inputs.
    // Example: "PERCENT12." parses "1" to 0.01
    // Example: "NLMNIJPY12." parses "(12)" but not "-12"
    if (this._isNumeric && this._formatName) {
      const parser = getFormatInstance('BEST32.');
      const parsed = parser?.parse(userInput) ?? NaN;
      // NaN means the user entered either a invalid or a formatted value.
      if (!isNaN(parsed)) {
        const formatter = getFormatInstance(this._formatName);
        valueToCommit = formatter?.format(parsed) ?? userInput;
      }
    }

    const inputDataValue = this._controller.getDataValueForUserInput(valueToCommit);

    // If required, error on empty input
    if (this._required && !inputDataValue) {
      return { key: 'TextFieldTypeAhead.validation.string.txt' };
    }

    if (inputDataValue) {
      if (this._isDateTime) {
        const parseFailed = isNaN(inputDataValue.getNumericValue());
        // validateValue returns null if there is a column but no matching data value.
        // VA checks this for dates but not other categorical data.
        const unmatchedInput = this._controller.validateValue(inputDataValue) === null;
        if (parseFailed || unmatchedInput) {
          return { key: 'TextFieldTypeAhead.validation.string.txt' };
        }
      }

      if (this._isNumeric) {
        // For measures treated categorically, VA doesn't error on a failed parse,
        // but BIRD raises warnings if we set the value on the controller.
        const parseFailed = isNaN(inputDataValue.getNumericValue());
        if (parseFailed) {
          return { key: 'TextFieldTypeAhead.validation.numeric.txt' };
        }
      }

      // If bounded, make sure the input is in bounds
      const min = this._minValue;
      const max = this._maxValue;
      if (!isNaN(min) && !isNaN(max)) {
        const value = inputDataValue.getNumericValue();
        if (value < min || value > max) {
          const formatter = getFormatInstance(this._formatName ?? 'BEST6');
          const formattedMin = formatter?.format(min) || min.toString();
          const formattedMax = formatter?.format(max) || max.toString();
          return {
            key: 'TextFieldTypeAhead.validation.range.fmt',
            args: { min: formattedMin, max: formattedMax },
          };
        }
      }
    }

    if (updateController) {
      this._ignoreValueChange = true;
      this._controller.setCurrentValue(inputDataValue);
      this._ignoreValueChange = false;
    }

    return null;
  }
}
